import React, { useEffect, useRef, useState } from 'react';

const GOOGLE_SCRIPT_ID = 'google-identity-services';
let scriptPromise;

function loadGoogleIdentityServices() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(GOOGLE_SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = GOOGLE_SCRIPT_ID;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => resolve(window.google), { once: true });
    script.addEventListener('error', () => {
      scriptPromise = null;
      reject(new Error('Google Identity Services could not be loaded.'));
    }, { once: true });
  });
  return scriptPromise;
}

export default function GoogleSignInButton({ onCredential, onError }) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const buttonRef = useRef(null);
  const credentialHandler = useRef(onCredential);
  const errorHandler = useRef(onError);
  const [loadError, setLoadError] = useState('');

  useEffect(() => { credentialHandler.current = onCredential; }, [onCredential]);
  useEffect(() => { errorHandler.current = onError; }, [onError]);

  useEffect(() => {
    if (!clientId) return undefined;
    let active = true;
    loadGoogleIdentityServices()
      .then((google) => {
        if (!active || !google?.accounts?.id || !buttonRef.current) return;
        google.accounts.id.initialize({
          client_id: clientId,
          auto_select: false,
          callback: async (response) => {
            if (!response?.credential) {
              errorHandler.current?.('Google did not return a sign-in credential.');
              return;
            }
            try {
              await credentialHandler.current(response.credential);
            } catch (error) {
              errorHandler.current?.(
                error.response?.data?.error || 'Google sign-in failed. Please try again.'
              );
            }
          },
        });
        buttonRef.current.replaceChildren();
        google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: 320,
        });
      })
      .catch((error) => {
        if (!active) return;
        const message = error.message || 'Google sign-in is unavailable.';
        setLoadError(message);
        errorHandler.current?.(message);
      });
    return () => { active = false; };
  }, [clientId]);

  if (!clientId) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div ref={buttonRef} aria-label="Continue with Google" />
      {loadError && <p style={{ color: '#e53935', fontSize: 12, marginTop: 8 }}>{loadError}</p>}
    </div>
  );
}
